import { html, PropertyValues, render, RootPart } from 'lit';
import { Constructor } from 'lit-element';
import { property, state } from 'lit/decorators.js';
import { GridColumnElement } from '@vaadin/vaadin-grid/vaadin-grid-column';
import { GridItemModel } from '@vaadin/vaadin-grid';
import { GzDataTableWebComponent } from '../component/GzDataTableWebComponent';
import { DataTableEvents, DataTableSelectionMode, DefaultColumnKeys, IDataTableColumn } from '../types';

// all public attributes and methods from the decorator class should be exposed in the interface
export interface RowSelectionInterface {
  allowRowSelection: boolean;
  selectItem(rowIndex: number, dispatchEvent?: boolean): void;
  deselectItem(rowIndex: number, dispatchEvent?: boolean): void;
  selectAll(dispatchEvent?: boolean): void;
  deselectAll(dispatchEvent?: boolean): void;
}

// all protected attributes and methods from the decorator class, used by other decorators, should be exposed here
export interface RowSelectionProtectedInterface {
  rowSelectionDisabled: boolean;
}

export const rowSelection = <T extends Constructor<GzDataTableWebComponent<TDataTableItem>>, TDataTableItem = unknown>(
  dataTableClass: T
) => {
  class RowSelectionDecorator extends dataTableClass {
    /**
     * If set, it allows selecting rows, based on the selection mode set in the <code>selectionMode</code> property
     */
    @property({ type: Boolean, attribute: true, reflect: true })
    allowRowSelection = false;

    @state()
    protected rowSelectionDisabled = false;

    /**
     * Defines the selection mode of the table; <code>DataTableSelectionMode.SINGLE</code> mode lets the user click on
     * a row to select it, while <code>DataTableSelectionMode.MULTI</code> mode displays checkboxes on the
     * <code>DefaultColumnKeys.SELECT</code> column
     */
    @property({ type: Object, attribute: true, reflect: true })
    selectionMode = DataTableSelectionMode.MULTI;

    @state()
    private selectedItems: Map<number, TDataTableItem> = new Map<number, TDataTableItem>(); // maps row index to item

    private readonly multiSelectColumnWidth = 40; // px

    firstUpdated(changedProperties: PropertyValues): void {
      super.firstUpdated(changedProperties);
      // active-item-changed is a custom event dispatched by vaadin-grid
      this.grid?.addEventListener('active-item-changed', this.onSingleSelectionChange.bind(this));
    }

    updated(changedProperties: PropertyValues): void {
      super.updated(changedProperties);
      if (changedProperties.has('currentItems') && changedProperties.get('currentItems') !== undefined) {
        if (this.currentItems.length !== changedProperties.get('currentItems').length) {
          this.deselectAll();
        }
      }
      if (changedProperties.has('selectionMode') && changedProperties.get('selectionMode') !== undefined) {
        this.deselectAll();
      }
    }

    selectItem(rowIndex: number, dispatchEvent = true): void {
      if (this.rowSelectionDisabled) {
        return;
      }
      if (this.selectionMode === DataTableSelectionMode.SINGLE) {
        this.selectedItems.clear();
      }
      this.selectedItems.set(rowIndex, this.currentItems[rowIndex]);
      this.onSelectedItemsUpdated(dispatchEvent);
    }

    deselectItem(rowIndex: number, dispatchEvent = true): void {
      if (this.rowSelectionDisabled || !this.selectedItems.has(rowIndex)) {
        return;
      }
      if (this.selectionMode === DataTableSelectionMode.SINGLE) {
        this.selectedItems.clear();
      } else {
        this.selectedItems.delete(rowIndex);
      }
      this.onSelectedItemsUpdated(dispatchEvent);
    }

    selectAll(dispatchEvent = true): void {
      if (this.rowSelectionDisabled || this.selectionMode === DataTableSelectionMode.SINGLE ) {
        return;
      }
      this.currentItems.forEach((item, index) => {
        this.selectedItems.set(index, item);
      });
      this.onSelectedItemsUpdated(dispatchEvent);
    }

    deselectAll(dispatchEvent = true): void {
      if (this.rowSelectionDisabled || this.selectedItems.size === 0) {
        return;
      }
      this.selectedItems.clear();
      this.onSelectedItemsUpdated(dispatchEvent);
    }

    protected renderColumn(column: IDataTableColumn, index: number): GridColumnElement {
      // overridden function
      const columnElement = super.renderColumn(column, index);
      if (
        this.allowRowSelection &&
        column.key === DefaultColumnKeys.MULTI_SELECT &&
        this.selectionMode === DataTableSelectionMode.MULTI
      ) {
        return this.renderMultiSelectionColumn(column, index);
      }
      return columnElement;
    }

    protected cellClassNameGenerator(columnElement: GridColumnElement, model: GridItemModel) {
      // overridden function
      let existingClasses = super.cellClassNameGenerator(columnElement, model);
      if (!this.allowRowSelection) {
        return existingClasses;
      }
      if (
        columnElement.path === DefaultColumnKeys.MULTI_SELECT &&
        this.selectionMode === DataTableSelectionMode.SINGLE
      ) {
        existingClasses += ' display-none-cell';
      }
      if (this.selectedItems.has(model.index)) {
        existingClasses += ' selected-cell';
      }
      return existingClasses;
    }

    private renderMultiSelectionColumn(column: IDataTableColumn, index: number): GridColumnElement {
      const columnElement = super.renderColumn(column, index);
      Object.assign(columnElement, {
        flexGrow: 0,
        frozen: true,
        width: this.multiSelectColumnWidth + 'px',
        resizable: false,
        autoWidth: false,
      });
      columnElement.headerRenderer = root => this.renderMultiSelectionHeaderCell(root);
      columnElement.renderer = (root, _, rowData) => this.renderMultiSelectionCell(rowData, root);
      return columnElement;
    }

    private renderMultiSelectionHeaderCell(root: HTMLElement): RootPart {
      const shouldCheck = this.selectedItems.size === 0;
      return render(
        html`<gz-checkbox
          label="."
          class="multi-select-cell"
          ?disabled="${this.currentItems.length === 0 || this.rowSelectionDisabled}"
          ?checked="${this.selectedItems.size === this.currentItems.length && this.currentItems.length > 0}"
          ?indeterminate="${this.selectedItems.size > 0 && this.selectedItems.size < this.currentItems.length}"
          @click="${() => this.handleHeaderCheckboxChange(shouldCheck)}"
        ></gz-checkbox>`,
        root
      );
    }

    private renderMultiSelectionCell(rowData: GridItemModel | undefined, root: HTMLElement): RootPart | undefined {
      if (!rowData) {
        return;
      }
      const checked = this.selectedItems.has(rowData.index);
      return render(
        html`<gz-checkbox
          label="."
          class="multi-select-cell"
          ?disabled="${this.rowSelectionDisabled}"
          ?checked="${checked}"
          @click="${() => this.handleRowCheckboxChange(!checked, rowData.index)}"
        ></gz-checkbox>`,
        root
      );
    }

    private handleHeaderCheckboxChange = (shouldCheck: boolean): void => {
      if (shouldCheck) {
        this.selectAll();
      } else {
        this.deselectAll();
      }
    };

    private handleRowCheckboxChange = (shouldCheck: boolean, rowIndex: number): void => {
      if (shouldCheck) {
        this.selectItem(rowIndex);
      } else {
        this.deselectItem(rowIndex);
      }
    };

    private onSelectedItemsUpdated(dispatchEvent: boolean): void {
      this.instance.rowReorderingDisabled =
        this.instance.rowEditingDisabled =
        this.instance.rowDeletionDisabled =
          this.selectedItems.size > 0;

      this.selectedItems = new Map(this.selectedItems); // trigger state update
      if (this.selectionMode === DataTableSelectionMode.MULTI) {
        this.instance.rowReorderingDisabled = this.selectedItems.size > 0;
      }
      if (dispatchEvent) {
        this.dispatchEvent(
          new CustomEvent(DataTableEvents.SELECTION_CHANGED, {
            detail: { items: this.selectedItems },
            bubbles: true,
            composed: true,
          })
        );
      }
    }

    private onSingleSelectionChange(event: CustomEvent): void {
      if (!this.allowRowSelection || this.selectionMode === DataTableSelectionMode.MULTI) {
        return;
      }
      const item = event.detail.value;
      if (!item) {
        this.deselectAll();
        return;
      }
      const index = this.currentItems.indexOf(item);
      if (index === -1) {
        return;
      }
      this.selectItem(index);
    }
  }

  return RowSelectionDecorator;
};
